import { Detailed, useGLTF, useTexture } from '@react-three/drei';
import React, { Suspense, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Bush } from './env/Bush';
import { HighChair } from './env/HighChair';
import { Plant } from './env/Plant';
import { Pot } from './env/Pot';
import { RoundedTable } from './env/RoundedTable';
import { RoundedTableSmall } from './env/RoundedTableSmall';
import { SmallChair } from './env/SmallChair';
import { Stool } from './env/Stool';
import { Umbrella } from './env/Umbrella';
import { WoodenChair } from './env/WoodenChair';

// Mobile detection
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Texture path helper
const getTexturePath = (textureName) => {
  return isMobile ? `/env/small/${textureName}` : `/env/baked/${textureName}`;
};

// Model path helper
const getModelPath = (modelName) => {
  if (modelName === 'Outside.glb') {
    return '/Outside.glb';
  }
  return isMobile ? `/env/small/${modelName}` : `/env/${modelName}`;
};

// Preload all textures for both mobile and desktop
const preloadTextures = () => {
  const texturePaths = [
    'Base V2.webp',
    'rocksStuff.webp',
    'Frames V2.webp',
    'FLOOR V2.webp',
    'floorBase V4.webp',
    'wooden.webp',
    'PATIO V2.webp',
    'WoodenChair V2.webp',
    'WoodenChairShades V2.webp',
    'chairSmall.webp',
    'chairSmallSHADE.webp',
    'chairSmallFRONT.webp',
    'chairSmallPool.webp',
    'chairSmallOcean.webp',
    'chairSmallRESTERAUNT.webp',
    'roundedTableOcean V2.webp',
    'roundedsmall V2.webp',
    'roundedTableSun V2.webp',
    'roundedTableOcean.webp',
    'Stool.webp',
    'PotV2.webp',
    'PotShades V2.webp',
    'mainPotPlant.webp',
    'mainPotPlantShades.webp',
    'Bushes V2.webp',
    'HighChair.webp',
    'HighChairAway.webp',
    'HighChairShades.webp'
  ];

  texturePaths.forEach(texture => {
    useTexture.preload(getTexturePath(texture));
  });
};

// Call preload function
preloadTextures();

// LOD distances for mobile and desktop
const LOD_DISTANCES = {
  mobile: {
    high: 0,
    medium: 15,
    low: 30
  },
  desktop: {
    high: 0,
    medium: 20,
    low: 40
  }
};

// LOD wrapper component
const ModelWithLOD = ({ children, distances }) => {
  const lodRef = useRef();
  
  return (
    <Detailed ref={lodRef} distances={distances}>
      {children}
    </Detailed>
  );
};

function OutsideContent() {
  const group = useRef();
  const { scene } = useGLTF('/Outside.glb');
  
  // Use the helper function to get the correct texture path
  const whiteBase = useTexture(getTexturePath('Base V2.webp'));
  whiteBase.colorSpace = THREE.SRGBColorSpace;
  whiteBase.flipY = false;
  whiteBase.generateMipmaps = isMobile ? false : false;
  whiteBase.channel = 1;

  const WoodnRock = useTexture(getTexturePath('rocksStuff.webp'));
  WoodnRock.colorSpace = THREE.SRGBColorSpace;
  WoodnRock.flipY = false;
  WoodnRock.generateMipmaps = false;
  WoodnRock.channel = 1;

  const Frames = useTexture(getTexturePath('Frames V2.webp'));
  Frames.colorSpace = THREE.SRGBColorSpace;
  Frames.flipY = false;
  Frames.channel = 1;

  const Floor = useTexture(getTexturePath('FLOOR V2.webp'));
  Floor.colorSpace = THREE.SRGBColorSpace;
  Floor.flipY = false;
  Floor.generateMipmaps = false;
  Floor.channel = 1;

  const BaseFloor = useTexture(getTexturePath('floorBase V4.webp'));
  BaseFloor.colorSpace = THREE.SRGBColorSpace;
  BaseFloor.flipY = false;
  BaseFloor.generateMipmaps = false;
  BaseFloor.channel = 1;
  
  const newWood = useTexture(getTexturePath('wooden.webp'));
  newWood.colorSpace = THREE.SRGBColorSpace;
  newWood.flipY = false;
  newWood.channel = 1;

  const Patio = useTexture(getTexturePath('PATIO V2.webp'));
  Patio.colorSpace = THREE.SRGBColorSpace;
  Patio.flipY = false;
  Patio.generateMipmaps = false;
  Patio.channel = 1;

  const lodDistances = isMobile ? LOD_DISTANCES.mobile : LOD_DISTANCES.desktop;
  useEffect(() => {
    scene.traverse((node) => {
      if (node.isMesh) {
        // Optimize materials for mobile
        if (isMobile) {
          node.material.precision = 'lowp';
          node.material.dithering = false;
          node.material.vertexColors = false;
          node.material.fog = false;
          node.material.shadowSide = null;
          node.material.toneMapped = false;
          
          // Reduce geometry detail for mobile
          if (node.geometry) {
            node.geometry.computeVertexNormals();
            node.geometry.attributes.normal.needsUpdate = true;
          }
        }

        if (node.name === 'Cube009') {
          node.material = new THREE.MeshBasicMaterial({
            map: whiteBase,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
        
        if (node.name.includes('Fence_full') ||node.name.includes('frames')) {
          node.material = new THREE.MeshStandardMaterial({
            color:0x000000,
            metalness:.8,
            roughness:0,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }

        if (node.name.includes('Door') ) {
          node.material = new THREE.MeshBasicMaterial({
            map: newWood,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }

        if (node.name === 'wood'||node.name === 'rocks'||node.name==='Cube'||node.name==='windows001') {
          node.material = new THREE.MeshBasicMaterial({
            map: WoodnRock,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }

        if (node.name === 'frames'||node.name === 'whiteBase002') {
          node.material = new THREE.MeshBasicMaterial({
            map: Frames,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
        if (node.name === 'floor'||node.name === 'stairs') {
          node.material = new THREE.MeshBasicMaterial({
            map: Floor,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
        if (node.name === 'floor') {
          node.material = new THREE.MeshBasicMaterial({
            map: Floor,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
        if (node.name.includes('water') ) {
          node.material.visible = false;
        }
        if (node.name === 'patio') {
          node.material = new THREE.MeshBasicMaterial({
            map: BaseFloor,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
 
        if (node.name === 'corner'||node.name === 'patio001') {
          node.material = new THREE.MeshBasicMaterial({
            map: Patio,
            toneMapped: false,
            side: THREE.DoubleSide,
          });
        }
        if (node.name === 'floor001') {
          node.material = new THREE.MeshBasicMaterial({
            map: Floor,
            toneMapped: false,
          });
        }
     
        if (node.name === 'windows') {
          node.material = new THREE.MeshBasicMaterial({
            toneMapped: false,
            side:THREE.DoubleSide,
           transparent:false, 
          color:0x000000,
          });
        }
      }
    });
  }, [scene]);

  return (
    <>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Umbrella jsonFile={'/jsons/umbrella.json'} texturePath={getTexturePath('umprella V2.webp')} />
      </ModelWithLOD>
   
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <WoodenChair jsonFile={'/jsons/woodenChair.json'} texturePath={getTexturePath('WoodenChair V2.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <WoodenChair jsonFile={'/jsons/woodenChairShades.json'} texturePath={getTexturePath('WoodenChairShades V2.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CHAIRS_FACING_RES.json'} texturePath={getTexturePath('chairSmall.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CHAIRS_IN_THE_SHADE.json'} texturePath={getTexturePath('chairSmallSHADE.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CHAIRS_FACING_FRONT.json'} texturePath={getTexturePath('chairSmallFRONT.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CHAIRS_FACING_POOL.json'} texturePath={getTexturePath('chairSmallPool.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CHAIRS_FACING_OCEAN.json'} texturePath={getTexturePath('chairSmallOcean.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <SmallChair jsonFile={'/jsons/CAHIR_FACING_RESTERAUNT.json'} texturePath={getTexturePath('chairSmallRESTERAUNT.webp')} />
      </ModelWithLOD>      
   
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <RoundedTable jsonFile={'/jsons/RoundedTable.json'} texturePath={getTexturePath('roundedTableOcean V2.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <RoundedTableSmall jsonFile={'/jsons/RoundedTableSmall.json'} texturePath={getTexturePath('roundedsmall V2.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <RoundedTable jsonFile={'/jsons/RoundedTableSun.json'} texturePath={getTexturePath('roundedTableSun V2.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <RoundedTable jsonFile={'/jsons/RoundedTableOcean.json'} texturePath={getTexturePath('roundedTableOcean.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Stool jsonFile={'/jsons/stoolsShades.json'} texturePath={getTexturePath('Stool.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Pot jsonFile={'/jsons/pot.json'} texturePath={getTexturePath('PotV2.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Pot jsonFile={'/jsons/potShades.json'} texturePath={getTexturePath('PotShades V2.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Plant jsonFile={'/jsons/plant.json'} texturePath={getTexturePath('mainPotPlant.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Plant jsonFile={'/jsons/plantShades.json'} texturePath={getTexturePath('mainPotPlantShades.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <Bush jsonFile={'/jsons/bush.json'} texturePath={getTexturePath('Bushes V2.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <HighChair jsonFile={'/jsons/highChair.json'} texturePath={getTexturePath('HighChair.webp')} />
      </ModelWithLOD>
      <ModelWithLOD distances={Object.values(lodDistances)}>
        <HighChair jsonFile={'/jsons/highChairAway.json'} texturePath={getTexturePath('HighChairAway.webp')} />
      </ModelWithLOD>

      <ModelWithLOD distances={Object.values(lodDistances)}>
        <HighChair jsonFile={'/jsons/highChairShades.json'} texturePath={getTexturePath('HighChairShades.webp')} />
      </ModelWithLOD>

      <primitive ref={group} object={scene} />
    </>
  );
}

// Main component with Suspense for loading
export default function Outside() {
  return (
    <Suspense fallback={null}>
      <OutsideContent />
    </Suspense>
  );
}
